import type {
  CommentResponseDto,
  CreateCommentDto,
  ListCommentsResponseDto,
} from '@inference.engine/workspace-api-client/web';
import type { InfiniteData } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import type { CommentAnnotationRef, CommentTargetRef } from '@/entities/project-comment';
import { isCommentsListKey, PROJECT_COMMENT_QUERY_KEYS } from '@/entities/project-comment';
import type { ProjectFileVersion } from '@/entities/project-file';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { syncFolderCommentCounts } from './sync-folder-comment-counts';
import { createComment } from './comments.api';
import { useTranslation } from 'react-i18next';

export { createComment };

interface UseCreateCommentOptions {
  projectId: string;
  target: CommentTargetRef;
}

function appendCreatedRootComment(comment: CommentResponseDto) {
  return (
    old: InfiniteData<ListCommentsResponseDto, number> | undefined,
  ): InfiniteData<ListCommentsResponseDto, number> | undefined => {
    if (!old || old.pages.length === 0) return old;
    const lastIdx = old.pages.length - 1;
    return {
      ...old,
      pages: old.pages.map((p, i) =>
        i === lastIdx
          ? { ...p, items: [...p.items, comment], total: p.total + 1 }
          : { ...p, total: p.total + 1 },
      ),
    };
  };
}

export function useCreateComment(options: UseCreateCommentOptions) {
  const { t } = useTranslation('projects');

  const queryClient = useQueryClient();

  const { projectId, target } = options;

  return useMutation({
    mutationFn: (body: CreateCommentDto) => createComment(projectId, target, body),
    onSuccess: (createdComment) => {
      // Bump the file/folder badge counts (root + replies both roll up).
      syncFolderCommentCounts(queryClient, projectId, target, 1);

      // Patch the annotations cache (overlay/highlight source) in place.
      if (createdComment.annotation) {
        const annotation = createdComment.annotation;
        queryClient.setQueryData<CommentAnnotationRef[]>(
          PROJECT_COMMENT_QUERY_KEYS.annotations(projectId, target.targetType, target.targetId),
          (old) => (old ? [...old, { commentId: createdComment.id, annotation }] : old),
        );
      }

      // Only root comments roll into the version's count.
      if (target.targetType === 'file' && !createdComment.parentId && createdComment.versionId) {
        const versionId = createdComment.versionId;
        queryClient.setQueryData<ProjectFileVersion[]>(
          PROJECT_FILE_QUERY_KEYS.versions(projectId, target.targetId),
          (old) =>
            old?.map((v) => (v.id === versionId ? { ...v, commentCount: v.commentCount + 1 } : v)),
        );
      }

      // Prefix + predicate, not setQueryData: the live key extends this version
      // prefix with a `{ sortOrder, showResolved }` suffix a bare write would miss.
      if (!createdComment.parentId) {
        queryClient.setQueriesData<InfiniteData<ListCommentsResponseDto, number>>(
          {
            queryKey: PROJECT_COMMENT_QUERY_KEYS.list(
              projectId,
              target.targetType,
              target.targetId,
              createdComment.versionId ?? undefined,
            ),
            predicate: (q) => isCommentsListKey(q.queryKey),
          },
          appendCreatedRootComment(createdComment),
        );
        return;
      }

      const parentId = createdComment.parentId;

      queryClient.setQueryData<ListCommentsResponseDto>(
        PROJECT_COMMENT_QUERY_KEYS.replies(projectId, target.targetType, target.targetId, parentId),
        (old) =>
          old ? { ...old, items: [...old.items, createdComment], total: old.total + 1 } : old,
      );

      // Parent may sit in any version-scoped list cache — bump it wherever found.
      queryClient.setQueriesData<InfiniteData<ListCommentsResponseDto, number>>(
        {
          queryKey: PROJECT_COMMENT_QUERY_KEYS.list(projectId, target.targetType, target.targetId),
          predicate: (q) => isCommentsListKey(q.queryKey),
        },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((c) =>
                c.id === parentId ? { ...c, replyCount: c.replyCount + 1 } : c,
              ),
            })),
          };
        },
      );
    },
    onError: (error) => {
      logger.error('Failed to create comment:', error);
      notificationService.showError(t('commentOnFile.useCreateComment.failedToPostComment'), {
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    },
  });
}
